/* ==========================================================================
   scroll-lock.js :: ONE page scroll lock for every full-screen overlay
   (cart drawer, PDP + Notebook lightbox, review-photo popup, email popup,
   PDP modals incl. guarantee, mobile nav, FAQ sheet). 2026-10-27.

   WHY THIS EXISTS (founder, real iPhone Safari, live site, 2026-10-27):
   scrolled partway down the PDP, the gallery lightbox and the cart drawer
   "pop up and assume I'm at the top of the page" -- the overlay sat high
   on the page, the page underneath kept scrolling, and the fixed overlay
   travelled with it.

   ROOT CAUSE: below 900px every overlay locked the page by setting
   overflow:hidden on <html> (html.popup-lock / inline style) and, for the
   cart and email popup, on <body> too. base.css gives html AND body
   overflow-x:clip, so that toggle swaps which box owns scrolling while the
   page is scrolled. On iPhone Safari the gesture still scrolled the page
   (overflow:hidden on the root is not a reliable touch lock there), and
   because WebKit now considered the root unscrollable, position:fixed
   layers were no longer compensated for that scroll -- they moved with
   the content. Desktop (>=900px) never toggled overflow (it blocked wheel
   / touch / keys instead) and never showed the bug.

   FIX: never touch html/body overflow. Block background scroll with
   events at every width -- the same idiom desktop already used -- and,
   for touch, only let a gesture through when it lands inside the
   overlay's own panel AND some element between the finger and that
   panel can actually scroll in the gesture's direction. At a scroller's
   end the gesture is cancelled instead of chaining to the page. The
   document scroll position is never changed, so closing returns the
   visitor exactly where they were, and sticky elements (desktop
   .pdp-gallery) are never un-stuck.

   API (ref-counted, idempotent per key):
     OrdinScrollLock.lock(key, scopeEl | [scopeEl...])
     OrdinScrollLock.unlock(key)
     OrdinScrollLock.isLocked()
   ========================================================================== */
(function () {
  'use strict';
  if (window.OrdinScrollLock) return;

  var holders = {};      /* key -> [scope elements] */
  var count = 0;
  var lastX = 0, lastY = 0;
  var NAV_KEYS = { ' ': 1, 'Spacebar': 1, 'PageUp': 1, 'PageDown': 1, 'Home': 1, 'End': 1, 'ArrowUp': 1, 'ArrowDown': 1 };

  function scopeFor(node) {
    if (!node || node.nodeType !== 1) node = node && node.parentElement;
    if (!node) return null;
    for (var k in holders) {
      var list = holders[k];
      for (var i = 0; i < list.length; i++) {
        if (list[i] && list[i].contains(node)) return list[i];
      }
    }
    return null;
  }

  /* Can anything from `node` up to (and including) `scope` scroll by
     (dx, dy)? dx/dy are CONTENT deltas: +dy = content moves to show more
     below (finger moves up / wheel down). */
  function canScroll(node, scope, dx, dy) {
    var vertical = Math.abs(dy) >= Math.abs(dx);
    for (var el = node; el && el.nodeType === 1; el = el.parentElement) {
      var cs = window.getComputedStyle(el);
      if (vertical) {
        if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
          if (dy > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
          if (dy < 0 && el.scrollTop > 0) return true;
          if (dy === 0) return true;
        }
      } else {
        if ((cs.overflowX === 'auto' || cs.overflowX === 'scroll') && el.scrollWidth > el.clientWidth + 1) {
          var sl = Math.abs(el.scrollLeft); /* RTL-safe enough for this theme */
          if (dx > 0 && sl + el.clientWidth < el.scrollWidth - 1) return true;
          if (dx < 0 && sl > 0) return true;
          if (dx === 0) return true;
        }
      }
      if (el === scope) break;
    }
    return false;
  }

  function isTextEntry(node) {
    if (!node || !node.tagName) return false;
    var t = node.tagName;
    return t === 'INPUT' || t === 'TEXTAREA' || t === 'SELECT' || node.isContentEditable === true;
  }

  function onTouchStart(e) {
    var t = e.touches && e.touches[0];
    if (t) { lastX = t.clientX; lastY = t.clientY; }
  }

  function onTouchMove(e) {
    if (!e.cancelable) return;
    /* two fingers: never let the browser pinch-zoom the page (that pans
       the visual viewport and drags fixed overlays). JS pinch handlers
       (the lightbox zoom) still receive the events. */
    if (e.touches && e.touches.length > 1) { e.preventDefault(); return; }
    var t = e.touches && e.touches[0];
    if (!t) return;
    var dx = lastX - t.clientX, dy = lastY - t.clientY;
    lastX = t.clientX; lastY = t.clientY;
    var scope = scopeFor(e.target);
    if (scope && canScroll(e.target, scope, dx, dy)) return;
    e.preventDefault();
  }

  function onWheel(e) {
    if (e.ctrlKey) { e.preventDefault(); return; } /* trackpad pinch */
    var scope = scopeFor(e.target);
    if (scope && canScroll(e.target, scope, e.deltaX, e.deltaY)) return;
    e.preventDefault();
  }

  function onKey(e) {
    if (!NAV_KEYS[e.key]) return;
    var t = e.target;
    if (isTextEntry(t) || scopeFor(t)) return;
    /* a focused control inside any overlay (close buttons) keeps Space */
    if (t && t.closest && t.closest('button, a[href], [role="button"]') && t.closest('[aria-modal="true"], [role="dialog"], [data-cart-drawer], [data-mobile-nav]')) return;
    e.preventDefault();
  }

  function attach() {
    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });
    document.addEventListener('keydown', onKey, true);
    document.documentElement.classList.add('is-scroll-locked');
    var lenis = window.Ordin && window.Ordin.lenis;
    if (lenis) lenis.stop();
  }

  function detach() {
    document.removeEventListener('touchstart', onTouchStart, true);
    document.removeEventListener('touchmove', onTouchMove, true);
    document.removeEventListener('wheel', onWheel, true);
    document.removeEventListener('keydown', onKey, true);
    document.documentElement.classList.remove('is-scroll-locked');
    var lenis = window.Ordin && window.Ordin.lenis;
    if (lenis) lenis.start();
  }

  window.OrdinScrollLock = {
    lock: function (key, scopes) {
      var list = !scopes ? [] : (scopes.length !== undefined && !scopes.nodeType ? Array.prototype.slice.call(scopes) : [scopes]);
      if (!holders[key]) { count++; if (count === 1) attach(); }
      holders[key] = list.filter(Boolean);
    },
    unlock: function (key) {
      if (!holders[key]) return;
      delete holders[key];
      count--;
      if (count === 0) detach();
    },
    isLocked: function () { return count > 0; }
  };
})();
